import React, { useState, useEffect } from 'react';
import './Features.css';

const Features: React.FC = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isInFeatures, setIsInFeatures] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const featuresSection = document.getElementById('features');
      if (!featuresSection) return;

      const featuresTop = featuresSection.offsetTop;
      const featuresBottom = featuresTop + featuresSection.offsetHeight;
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      // Check if we're in the features section
      setIsInFeatures(scrollPosition >= featuresTop && scrollPosition < featuresBottom);

      // Update active section
      const sections = document.querySelectorAll('.feature-row-full-white, .feature-row-dark');
      sections.forEach((section, index) => {
        const element = section as HTMLElement;
        const top = element.offsetTop;
        const bottom = top + element.offsetHeight;

        if (scrollPosition >= top && scrollPosition < bottom) {
          setActiveSection(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // GTM Trigger: Track section views
  useEffect(() => {
    if ((window as any).dataLayer && isInFeatures) {
      (window as any).dataLayer.push({
        event: 'section_view',
        section_number: activeSection + 1,
        section_name: `Feature Section 0${activeSection + 1}`,
      });
    }
  }, [activeSection, isInFeatures]);

  const scrollToSection = (index: number) => {
    const sections = document.querySelectorAll('.feature-row-full-white, .feature-row-dark');
    const section = sections[index] as HTMLElement;
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="features" className="features">
      {/* Section Navigation - only show when in features section */}
      {isInFeatures && (
        <div className="section-navigation">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <button
              key={index}
              className={`section-nav-item ${activeSection === index ? 'active' : ''}`}
              onClick={() => scrollToSection(index)}
              data-event={`nav_dot_section_0${index + 1}`}
              aria-label={`Go to section ${index + 1}`}
            >
              <span className="section-number">0{index + 1}</span>
            </button>
          ))}
        </div>
      )}

      <div className="feature-row-full-white">
        <h2 className="features-title">Lleva tus finanzas al siguiente nivel 🚀</h2>
        <div className="feature-section">
          <div className="feature-text">
            <p className="feature-tag">01 · Tu Asistente IA</p>
            <h3>Apóyate en Rillo, tu copiloto financiero</h3>
            <p>
              Utiliza nuestro asistente Rillo para resolver tus dudas sobre tus finanzas en segundos.
              ¿Quieres comprarte un coche? Rillo analiza tu situación real para decirte si es viable y te ofrece tres planes personalizados para lograrlo sin comprometer tu salud financiera.
            </p>
            <a href="https://app.findbalance.es" className="btn" data-event="features_rillo_cta" style={{ marginTop: '20px', display: 'inline-block' }}>Prueba Rillo gratis</a>
          </div>
          <div className="feature-media">
            <picture>
              <source media="(max-width: 768px)" srcSet="rillomobile.png" />
              <img src="rillo.png" alt="Asistente Rillo IA - Planificación financiera personalizada con inteligencia artificial" className="feature-img feature-img-small" />
            </picture>
          </div>
        </div>
      </div>

      <div className="feature-row-dark">
        <div className="feature-section feature-section-reverse">
          <div className="feature-text">
            <p className="feature-tag">02 · Ahorro Inteligente</p>
            <h3>Ahorra de forma fácil y sin complicaciones</h3>
            <p>
              Crea tantas cuentas de ahorro como necesites y define objetivos claros para cada una.
            </p>
          </div>
          <div className="feature-media">
            <img src="ahorro.png" alt="Gestión de ahorros y objetivos financieros en Find Balance" className="feature-img" />
          </div>
        </div>
      </div>

      <div className="feature-row-full-white">
        <div className="feature-section">
          <div className="feature-text">
            <p className="feature-tag">03 · Control de Gastos</p>
            <h3>Ve el impacto de cada euro en tiempo real</h3>
            <p>
              Analiza tus gastos al momento, detecta patrones y recibe una visión clara de en qué se va tu dinero para
              tomar mejores decisiones día a día.
            </p>
          </div>
          <div className="feature-media feature-media-left-fade multiple-images">
            <img src="gastos_variables.png" alt="Gráfico de control de gastos mensuales y patrones de consumo" className="feature-img" />
          </div>
        </div>
      </div>

      <div className="feature-row-dark">
        <div className="feature-section feature-section-reverse">
          <div className="feature-text">
            <p className="feature-tag">04 · Inversiones Simplificadas</p>
            <h3>De la intuición a la estrategia</h3>
            <p>
              Evalúa tu cartera, simula escenarios y entiende el riesgo de tus movimientos para invertir con más calma y
              menos improvisación.
            </p>
          </div>
          <div className="multiple-images">
            <img src="inversion.png" alt="Seguimiento de inversiones y cartera de activos" className="feature-img" />
            <img src="operaciones.png" alt="Historial de operaciones e inversiones financieras" className="feature-img" />
          </div>
        </div>
      </div>

      <div className="feature-row-full-white">
        <div className="feature-section">
          <div className="feature-text">
            <p className="feature-tag">05 · Visión Clara</p>
            <h3>Una sola pantalla para ver tu futuro financiero</h3>
            <p>
              Consolida ahorros, gastos e inversiones en una vista limpia y futurista que te muestra hacia dónde vas y qué
              puedes mejorar hoy.
            </p>
          </div>
          <div className="multiple-images">
            <img src="sumary.png" alt="Resumen patrimonial completo: ahorros, gastos e inversiones" className="feature-img" />
            <img src="health.png" alt="Indicadores de salud financiera y recomendaciones" className="feature-img" />
          </div>
        </div>
      </div>

      <div className="feature-row-dark">
        <div className="feature-section feature-section-reverse">
          <div className="feature-text">
            <p className="feature-tag">06 · ¡Adiós Excel, hola Find Balance! 👋</p>
            <h3>Se acabaron los Excel, comienza de nuevo</h3>
            <p>
              Olvídate de fórmulas rotas, pestañas infinitas y archivos que nunca están actualizados.
              Con Find Balance ves tus movimientos, resúmenes y previsiones en una interfaz viva, bonita y siempre al día.
            </p>
            <a href="https://app.findbalance.es" className="btn" data-event="features_excel_cta" style={{ marginTop: '20px', display: 'inline-block' }}>Deja el Excel hoy mismo</a>
          </div>
          <div className="feature-media">
            <img src="dashboard.png" alt="Dashboard principal de Find Balance - Control total de finanzas" className="feature-img" />
          </div>
        </div>
      </div>
    </section >
  );
};

export default Features;